"use client";

import React from "react";

import CornerChip from "@/components/global/ui/CornerChip";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";
import {AnimatedAsset1} from "@/components/global/animatedAssets/Asset1";
import {AnimatedAsset2} from "@/components/global/animatedAssets/Asset2";
import {AnimatedAsset3} from "@/components/global/animatedAssets/Asset3";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import {PageCommunitySchema} from "@/sanity/pages/PageCommunityTemplate";

const animationMap = [AnimatedAsset1, AnimatedAsset2, AnimatedAsset3];

export default function ExceptSection({
  data,
  className
}: {
  data: PageCommunitySchema;
  className?: string;
}) {

  return (
    <section className={cn(
      "relative overflow-clip pb-[13rem] lg:pb-[25rem]",
      className
    )}>
      {data?.expectScroll &&
        <ScrollingBanner
          text={data.expectScroll}
        />
      }

      <div className="relative overflow-clip pt-[13rem] lg:pt-[15rem]">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />
        <DecorationLine
          className="top-0"
          orientation="horizontal"
        />

        <DecorationSquares
          className="top-[2px]"
        />

        <div className="container relative flex flex-col items-center">
          <Chip
            image={data?.expectChip?.icon}
            text={data?.expectChip?.text}
          />

          {data?.expectText && (
            <div className="richtext prose-p:w-[min(75rem,100%)] prose-headings:w-[min(120rem,100%)] mb-[4rem]">
              <PortableText
                value={data.expectText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.expectItems && (
            <div className="w-[min(166rem,100%)] flex flex-col items-center lg:items-stretch lg:grid lg:grid-cols-3 gap-[2rem]">
              {data.expectItems.map((item, index) => {
                const AnimatedAsset = animationMap[index] || null;
                return (
                  <div
                    key={index}
                    className="bg-white w-[min(57.5rem,100%)] lg:w-full"
                  >
                    {AnimatedAsset && (
                      <AnimatedAsset language={data?.settings?.language || "de"} />
                    )}

                    <div className="relative p-[3rem] sm:p-[4rem]">
                      <CornerChip
                        className="absolute -left-[1px] -translate-y-px bottom-full"
                        image={item?.chip?.icon}
                        text={item?.chip?.text}
                      />

                      {item?.text && (
                        <div className="richtext richtext-start prose-p:text-black/80">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data?.expectCTA && (
            <div className="w-[min(57.5rem,100%)] lg:w-[min(166rem,100%)] bg-white mt-[2rem] flex flex-col lg:flex-row items-center lg:justify-between gap-[2rem] px-[2.5rem] pt-[3rem] pb-[2rem] sm:px-[3rem] lg:p-[1rem]">
              <p className="lg:pl-[2rem] mb-0 text-black/80 text-[2.5rem] text-center lg:text-start">{data?.expectCTAText}</p>

              <CTACornerButton
                data={data.heroCTA}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
